/**
 * Contract between the main process and the hidden `challenge` renderer that
 * hosts Arkose.
 *
 * Arkose's client API is a browser script, so it has to live in a renderer. It
 * still belongs to exactly one of them: Arkose asks that the script load once
 * per page, and every extra enforcement instance is a separately billed
 * assessment. One window owns it and main brokers access.
 */

export const CHALLENGE_RENDERER_NAME = 'challenge'

/** LaunchDarkly flag that turns the challenge on for a user. */
export const CHALLENGE_FLAG_KEY = 'enable-arkose-challenge'

/** GlobalState key the challenge renderer sets once Arkose is configured. */
export const CHALLENGE_READY_KEY = 'challenge.ready'

/**
 * main -> challenge renderer: run a challenge, resolve with a token.
 *
 * Carries the gateway-minted Data Exchange blob when one is available, then
 * whether the run is a background one. Sent to the challenge window alone,
 * never broadcast: the reply carries a single-use token and the request a blob
 * that names the user.
 */
export const CHALLENGE_RUN_EVENT = 'challenge:run'

/**
 * main -> challenge renderer: end the in-flight challenge with the given
 * {@link ChallengeEndReason}.
 */
export const CHALLENGE_CANCEL_EVENT = 'challenge:cancel'

/**
 * Why main is ending a run early. `cancelled` is the caller giving up (the turn
 * was aborted). `abandoned` is the user closing the window, the same outcome as
 * dismissing the puzzle inside it. `disabled` is the feature flag turning off
 * mid-run: the turn goes ahead without a token.
 */
export type ChallengeEndReason = 'cancelled' | 'abandoned' | 'disabled'

/** challenge renderer -> main: Arkose wants to show/hide the puzzle. */
export const CHALLENGE_VISIBILITY_EVENT = 'challenge:visibility'

export type ChallengeFailureReason =
  /** The challenge window never came up, or Arkose never finished loading. */
  | 'unavailable'
  /** Arkose neither finished nor showed a puzzle in time, or a shown puzzle ran past its deadline. */
  | 'timeout'
  /** The user dismissed the puzzle without solving it. */
  | 'abandoned'
  /** Arkose reported the user failed the challenge. */
  | 'failed'
  /** Arkose reported an internal/network error. */
  | 'error'
  /** The caller aborted (turn cancelled) before the challenge resolved. */
  | 'cancelled'
  /**
   * The feature flag is off in main. Not a failure for the turn: the caller
   * sends without a token, exactly as an unflagged client does.
   */
  | 'disabled'
  /**
   * A background run reached a visible puzzle and ended rather than open the
   * window. The send runs its own.
   */
  | 'puzzle-required'

/**
 * Challenges resolve rather than throw. An Error crossing the GlobalEvents
 * boundary arrives as a bare message with nothing left to branch on, and
 * callers need the reason to pick their copy and telemetry.
 */
export type ChallengeResult = (
  | { ok: true; token: string }
  | { ok: false; reason: ChallengeFailureReason; message: string }
) &
  Partial<ChallengeRunFacts>

/**
 * What Arkose did during a run, for telemetry. Absent when the run never
 * reached Arkose, e.g. the flag was off or the window never loaded.
 */
export type ChallengeRunFacts = {
  /** The puzzle window was put on screen. */
  shown: boolean
  /** Arkose judged the session safe and skipped the challenge. */
  suppressed: boolean
  /** Times Arkose called `onWarning`. The run carries on through them. */
  warnings: number
}

export const challengeFailure = (reason: ChallengeFailureReason, message: string): ChallengeResult => ({
  ok: false,
  reason,
  message,
})

/**
 * How long Arkose has after `run()` to either finish or put a puzzle on screen.
 * A backstop for stalls main can't see. A cold run in a hidden window took
 * 1.25 s when measured on 2026-10-23. `onShow` doesn't count, because Arkose
 * fires it on every run, even one that then goes silent.
 */
export const CHALLENGE_HIDDEN_RUN_TIMEOUT_MS = 10_000

/**
 * Deadline once Arkose has shown a puzzle. Long, because someone may be
 * part-way through solving it and cutting them off is worse than the wait.
 */
export const CHALLENGE_INTERACTIVE_TIMEOUT_MS = 120_000

/**
 * Arkose stalled or errored before putting a puzzle on screen. Main reloads its
 * script after these, so another run can pass where this one couldn't. A run
 * that showed a puzzle had someone at it, and repeating it would hand them
 * another puzzle and another two minutes.
 */
export function isFixedByReloadingArkose(result: ChallengeResult): result is ChallengeResult & { ok: false } {
  return !result.ok && !result.shown && (result.reason === 'timeout' || result.reason === 'error')
}

/**
 * How long main waits for the renderer to report Arkose ready before failing a
 * run. Has to cover a cold window: page load plus the vendor script fetch.
 */
export const CHALLENGE_READY_TIMEOUT_MS = 20_000

/**
 * How long a token minted ahead of a send stays usable. Arkose verified a
 * 15-minute-old token as solved on 2026-10-13, so 10 leaves a margin.
 */
export const CHALLENGE_PREFETCH_MAX_AGE_MS = 10 * 60_000

/** How long to wait for `onReady` after `reset()` before sending it again; a healthy reload reports ready well inside a second. */
export const CHALLENGE_RESET_RETRY_MS = 1_500
